
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Mail, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type PendingInvitation = {
  id: string;
  email: string;
  status: string;
  organization_id: string;
};

type EmailInputSectionProps = {
  emailList: string[];
  setEmailList: (emails: string[]) => void;
  remainingInvites: number;
  isLoading: boolean;
  selectedOrgId: string;
  pendingInvites: PendingInvitation[];
};

const EmailInputSection = ({
  emailList,
  setEmailList,
  remainingInvites,
  isLoading,
  selectedOrgId,
  pendingInvites,
}: EmailInputSectionProps) => {
  const [emailInput, setEmailInput] = useState("");
  const { toast } = useToast();

  // Gérer l'ajout d'un e-mail à la liste
  const handleAddEmail = (email: string) => {
    if (!email.trim()) return;
    
    // Validation basique d'e-mail
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Format d'e-mail invalide",
        description: "Veuillez entrer une adresse e-mail valide.",
        variant: "destructive",
      });
      return;
    }

    // Vérifier si l'e-mail existe déjà dans la liste
    if (emailList.includes(email.trim())) {
      toast({
        title: "E-mail déjà ajouté",
        description: "Cette adresse e-mail est déjà dans la liste.",
        variant: "destructive",
      });
      return;
    }

    // Vérifier si l'utilisateur a déjà été invité
    if (pendingInvites.some(invite => invite.email === email.trim())) {
      toast({
        title: "Invitation déjà envoyée",
        description: "Cet utilisateur a déjà une invitation en attente.",
        variant: "destructive",
      });
      return;
    }

    // Vérifier si on a assez d'invitations restantes
    if (emailList.length >= remainingInvites) {
      toast({
        title: "Limite d'invitations atteinte",
        description: `Votre plan limite le nombre d'utilisateurs.`,
        variant: "destructive",
      });
      return;
    }

    setEmailList([...emailList, email.trim()]);
    setEmailInput("");
  };

  // Gérer le retrait d'un e-mail de la liste
  const handleRemoveEmail = (email: string) => {
    setEmailList(emailList.filter(e => e !== email));
  };

  return (
    <div className="mb-6">
      <Label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-1">
        Adresses e-mail
      </Label>
      <p className="text-xs text-gray-500 mb-2">
        Séparez les e-mails par une virgule, une tabulation, un espace ou cliquez sur Entrée après chaque email
      </p>
      
      {/* Zone de saisie d'e-mails */}
      <div className="relative">
        <Textarea
          id="email-input"
          placeholder="exemple@email.com"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === 'Tab') {
              e.preventDefault();
              if (emailInput.trim()) {
                handleAddEmail(emailInput.trim());
              }
            }
          }}
          className="min-h-[100px] mb-2"
          disabled={remainingInvites === 0 || isLoading || !selectedOrgId}
        />
        {emailList.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {emailList.map((email) => (
              <Badge key={email} className="bg-blue-100 text-blue-700 hover:bg-blue-200 flex items-center gap-1 py-1 px-2">
                <Mail className="h-3 w-3" />
                {email}
                <button 
                  onClick={() => handleRemoveEmail(email)} 
                  className="ml-1 text-blue-500 hover:text-blue-700"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-500">
          {emailList.length} / {remainingInvites} invitation{remainingInvites !== 1 ? 's' : ''}
        </div>
        <Button 
          variant="outline" 
          className="text-blue-500 hover:text-blue-700 border-blue-200 hover:border-blue-300"
          onClick={() => setEmailList([])}
          disabled={emailList.length === 0 || isLoading}
        >
          Effacer les e-mails
        </Button>
      </div>
    </div>
  );
};

export default EmailInputSection;
